/** Rotating taglines for the workflow processing view. */
export const LOADING_TAGLINES: readonly string[] = [
  "Reading the fine print so you don't have to…",
  "Comparing every dotted line…",
  "Crunching deductibles…",
  "Looking for the gotchas…",
  "Peeking at the endorsements…",
  "Weighing what actually changed…",
  "Separating the material from the noise…",
  "Drafting the talking points…",
];

// ---------------------------------------------------------------------------
// Already-answered / refusal copy (emuw-v1-5 `shared-decision-components`).
//
// Resolves PRD Open Question 7. Every string either surface shows for a refusal
// comes from HERE. A literal in either host is the wording drift this story
// exists to end — "same wording everywhere" has to be testable rather than a
// matter of implementer taste, and it only is if there is one place to look.
// ---------------------------------------------------------------------------

/** Heading on the already-answered card. */
export const DECISION_ALREADY_ANSWERED_TITLE = "Already answered";

/**
 * Body when the recorded answer is known. `answer` comes from
 * `summarizeHitlResponse()` over the hitl-history event for this step.
 */
export const decisionAlreadyAnsweredBody = (answer: string): string =>
  `This decision was already answered with “${answer}”.`;

/**
 * Body when the answer cannot be resolved — history not loaded yet, or the event
 * carries no response. Never leave the AM with a bare heading and nothing under it.
 */
export const DECISION_ALREADY_ANSWERED_BODY_UNKNOWN =
  "This decision was already answered on another surface.";

/**
 * Shown where the AM's OWN submission was refused.
 *
 * States BOTH facts on purpose: that their click did not land, and that nothing
 * further will be sent. AC-4 requires the AM be TOLD — a button that quietly stops
 * working leaves them clicking and guessing, which is the behaviour being fixed.
 */
export const decisionRefusedBody = (answer: string | null): string =>
  answer
    ? `Not sent — this decision was already answered with “${answer}”. Nothing further will be submitted for it.`
    : "Not sent — this decision was already answered on another surface. Nothing further will be submitted for it.";

// ---------------------------------------------------------------------------
// Automated-runs KPI copy.
//
// ONE wording for the figure `status.count_automated_runs` produces, so the ora
// Monitor's card and the plugin pane's row read the same. The pane used to say
// "N triaged today" for it — wrong twice over: it counts completed automated
// RUNS rather than triaged emails, and the window is a rolling 24 hours, not a
// calendar day (the substrate computes in UTC and the caller's identity carries
// no timezone, so a calendar day would roll over mid-afternoon for HSIP and the
// two surfaces would stop agreeing).
// ---------------------------------------------------------------------------

/** Title of the Monitor's card for this figure. */
export const AUTOMATED_KPI_TITLE = "Automated";

/**
 * What the figure means, in ora's own words. `window` is the window the SERVER
 * reported computing over, never the one the client asked for — the card has
 * always described what it actually got.
 */
export const automatedKpiWindowCopy = (window: string): string =>
  `completed in last ${window === "1d" ? "24 hours" : "7 days"}`;

// ---------------------------------------------------------------------------
// Carrier-evidence panel — the guidance and busy copy `CarrierEvidencePanel`
// renders. The four PRESENTATION strings stay in `carrierEvidenceCopy.ts`
// (they are PRD-binding and probe-asserted); these are the strings that panel
// adds, and they live here for the same reason: one place to look.
// ---------------------------------------------------------------------------

/**
 * Shown the moment the AM takes the branch over, before the server has
 * confirmed it. It names the next action, which is the whole complaint the
 * panel exists to answer: the old card disabled its own button and said
 * nothing about what to do next.
 */
export const EVIDENCE_TAKEOVER_GUIDANCE =
  "Drop the declarations page below, then Stage evidence. " +
  "The run keeps working on Epic meanwhile.";

/** While the branch switch is in flight. Accompanies the label, never replaces it. */
export const EVIDENCE_TAKEOVER_BUSY = "Switching to you supplying the evidence…";

/** While a file is being staged. */
export const EVIDENCE_STAGE_BUSY = "Staging…";

/** While the parked gate is being answered with the documents. */
export const EVIDENCE_APPROVE_BUSY = "Submitting…";

/** While the branch is being handed back to the carrier. */
export const EVIDENCE_REVERT_BUSY = "Handing back to the carrier…";

/**
 * The branch switch was accepted but the run has not caught up yet. Replaces a
 * busy state that would otherwise sit blank and disabled forever: the panel
 * never claims the branch changed (only a snapshot may say that), but it must
 * not trap the AM either.
 */
export const EVIDENCE_TAKEOVER_SLOW =
  "Still switching — the run will catch up. You can attach the file now.";

/** How long the panel waits for the snapshot before showing the line above. */
export const EVIDENCE_TAKEOVER_SLOW_AFTER_MS = 10_000;

// ---------------------------------------------------------------------------
// Monitor Nudge (leader governance phase 3, D9). One place for the two
// strings the action route and the Runs table both show.
// ---------------------------------------------------------------------------

/** A nudge that did not go out. Nothing is logged on the run in that case. */
export const NUDGE_FAILED = "That reminder didn't go out, and nothing was logged.";

/** The Runs cell after a nudge: one short line; the details live in its tooltip (2026-10-02). */
export const NUDGE_SENT = "Reminder sent";
/** The Runs cell when the 4-hour limit refused a second nudge: no new email went out. */
export const NUDGE_ALREADY = "Already reminded";

// ---------------------------------------------------------------------------
// Monitor Mailboxes tile (ADO #26760/#26763). ONE sub-line wording, so the
// Needs-You row's Mailboxes tile and (should the same figure ever be quoted
// elsewhere) a second surface cannot describe the same health block two
// different ways.
// ---------------------------------------------------------------------------

/**
 * The Mailboxes tile's sub-line. Demoted and awaiting-branch counts are named
 * SEPARATELY, never combined into one number — they are different problems
 * with different fixes (a demotion is a posture failure the sync found; an
 * awaiting-branch mailbox has simply never been assigned one), and folding
 * them together would send whoever reads the tile to look for one issue when
 * there may be two, or hide the rarer of the two behind the more common one.
 *
 * `null` health (the read failed, timed out, or an older substrate build
 * sent nothing) reads as "health unavailable" — never a silently blank line,
 * which would look like "nothing to report" over a read that in fact told us
 * nothing.
 */
export function mailboxesAttentionCopy(
  health: { demoted_count?: number; awaiting_branch_count: number } | null,
  notificationFailures: number,
): string {
  if (!health) return "health unavailable";

  const parts: string[] = [];
  const demoted = health.demoted_count ?? 0;
  if (demoted > 0) {
    parts.push(`${demoted} demoted`);
  }
  if (health.awaiting_branch_count > 0) {
    parts.push(`${health.awaiting_branch_count} awaiting branch`);
  }
  if (notificationFailures > 0) {
    parts.push(
      `${notificationFailures} notification${notificationFailures === 1 ? "" : "s"} failed`,
    );
  }

  return parts.length > 0 ? parts.join(" · ") : "all healthy";
}

/**
 * Shown once the server says evidence is attached. It answers the question the
 * surface previously left open — the operator staged two files, the upload
 * completed, and the card went on showing an empty dropzone with "Drop the
 * declarations page below, then Stage evidence" under a body that said the
 * evidence was already attached. Two contradictory instructions and no
 * statement of what happens next.
 */
export const EVIDENCE_ATTACHED_NEXT =
  "Nothing further needed — ora attaches this to the endorsement and the reply " +
  "when the run reaches the evidence step.";

/**
 * The attached-but-unnamed case: the server reports evidence attached and the
 * per-document list has not arrived (an older substrate, or a snapshot taken
 * between the stage and the next poll). Saying so is honest; re-inviting an
 * upload is not.
 */
export const EVIDENCE_ATTACHED_UNNAMED = "Evidence is attached to this run.";

/** Why the submit is not available yet. A disabled control with no reason is
 *  the defect this panel was rewritten to remove; it must not reappear on the
 *  submit button just because it was fixed on the branch button. */
export const EVIDENCE_SUBMIT_NEEDS_FILE = "Choose a file to stage first.";
